import { ReadableAtom, WritableAtom, action, atom, computed } from 'nanostores';
import { parseSerializedData } from '../../general/utils/consultation/serialize-form';
import { FormValues, ParsedFormValues } from '../types';
import { BookConsultationPayload, CalendarType, IncomeKey, InsuranceCategory, InsuranceGroup, Interest, Language, OccupationStatus } from '../../types/consultation';
import { pageSettings } from '../../general/utils/ga4-tracking/page-settings';

export type StoreOptions = {
  isExpat?: boolean;
  withCalendar?: boolean;
  leadSource?: string;
};

const pageDefaults = {
  insurance_category: isOfEnum(pageSettings.insuranceCategory, InsuranceCategory) ? (pageSettings.insuranceCategory as InsuranceCategory) : InsuranceCategory.Unknown,
  insurance_group: isOfEnum(pageSettings.insuranceGroup, InsuranceGroup) ? (pageSettings.insuranceGroup as InsuranceGroup) : InsuranceGroup.Unknown,
  ...(isOfEnum(pageSettings.jobStatus, OccupationStatus) && { employment: pageSettings.jobStatus as OccupationStatus }),
  language: en ? Language.English : Language.German
};

export default function createFormStore(defaults: FormValues = {}, options: StoreOptions = {}) {
  const initialFormvalues = { lead_source: options.leadSource, ...sanitizeValues(defaults) };

  const initialOptions = {
    isExpat: pageSettings.isExpat,
    ...options
  };

  let implizitFields = new Set(Object.keys(defaults));

  const formValues = atom<FormValues>(initialFormvalues);
  const parsed = computed<ParsedFormValues, ReadableAtom<FormValues>>(formValues, formValues => parseSerializedData(formValues) as ParsedFormValues);

  const submit = action(formValues, 'submit', (formValues, values: FormValues, withPageDefaults?: boolean) => {
    implizitFields = new Set([...implizitFields.values(), ...Object.keys(values)]);
    let newFormValues = { ...(withPageDefaults && pageDefaults), ...formValues.get(), ...applyFallBacks(values) };

    formValues.set(sanitizeValues(newFormValues, initialOptions));

    // Update window.craftConfig.jobStatus when employment changes
    if (values.employment && (window as any).craftConfig) {
      (window as any).craftConfig.jobStatus = values.employment;
    }
  });

  return {
    values: formValues as WritableAtom<FormValues>,
    parsed,
    get implizitFields() {
      return implizitFields;
    },
    submit
  };
}

export function formValuesToConsultationPayload(values: ParsedFormValues): BookConsultationPayload {
  const {
    first_name,
    last_name,
    email,
    phone_number,
    date_of_birth,
    language,
    income,
    employment,
    jaeg_reached,
    insurance_group,
    appointment,
    calendar,
    otto_solution_key,
    lead_source,
    customer_type,
    consultation_language
  } = values;

  return {
    contact_details: {
      first_name,
      last_name,
      email,
      phone_number,
      date_of_birth,
      landing_page_language: language,
      insurance_group,
      ...(lead_source && { lead_source }),
      ...(jaeg_reached && { income: IncomeKey.More }),
      ...(income && { income }),
      ...(employment && { employment }),
      ...(customer_type && { customer_type }),
      ...(consultation_language && { consultation_language })
    },
    ...(otto_solution_key && { otto_solution_key, verify_captcha: true }),
    ...(appointment && calendar && { appointment, calendar })
  };
}

export type FormStore = ReturnType<typeof createFormStore>;

/* ---------- private helpers ---------- */

function sanitizeValues(formValues: FormValues, options: StoreOptions = {}): FormValues {
  const adaptedValues: FormValues = { ...formValues };

  //switch full and residual category if category and employment combination is invalid
  const { insurance_category, employment } = adaptedValues;
  if (insurance_category && employment) {
    if (insurance_category === InsuranceCategory.Full && (employment === OccupationStatus.CivilServant || employment === OccupationStatus.CivilServantApplicant)) {
      adaptedValues.insurance_category = InsuranceCategory.Residual;
    }
    if (insurance_category === InsuranceCategory.Residual && employment !== OccupationStatus.CivilServant && employment !== OccupationStatus.CivilServantApplicant) {
      adaptedValues.insurance_category = InsuranceCategory.Full;
    }
  }

  if (adaptedValues.insurance_category) {
    switch (adaptedValues.insurance_category) {
      case InsuranceCategory.Clinic:
        adaptedValues.insurance_group = InsuranceGroup.Clinic;
        break;
      case InsuranceCategory.Dental:
        adaptedValues.insurance_group = InsuranceGroup.Dental;
        break;
      case InsuranceCategory.Full:
        switch (employment) {
          case OccupationStatus.Employed:
          case OccupationStatus.SelfEmployed:
            if (options.isExpat) {
              adaptedValues.insurance_group = InsuranceGroup.Expat;
              break;
            }
            adaptedValues.insurance_group = InsuranceGroup.EmployeeOrSelfEmployed;
            break;
          case OccupationStatus.Student:
            adaptedValues.insurance_group = InsuranceGroup.Student;
            break;
          default:
            adaptedValues.insurance_group = InsuranceGroup.Unknown;
            break;
        }
        break;
      case InsuranceCategory.Residual:
        switch (employment) {
          case OccupationStatus.CivilServant:
            adaptedValues.insurance_group = InsuranceGroup.CivilServant;
            break;
          case OccupationStatus.CivilServantApplicant:
            adaptedValues.insurance_group = InsuranceGroup.CivilServantApplicant;
            break;
          default:
            adaptedValues.insurance_group = InsuranceGroup.Unknown;
            break;
        }
        break;
      default:
        adaptedValues.insurance_group = InsuranceGroup.Unknown;
    }
  }

  if (adaptedValues.insurance_group === InsuranceGroup.Unknown && (!adaptedValues.insurance_category || adaptedValues.insurance_category === pageDefaults.insurance_category)) {
    adaptedValues.insurance_group = pageDefaults.insurance_group;
  }

  if (options.withCalendar) {
    adaptedValues.calendar = getCalendarFromCategory(adaptedValues.insurance_category || pageDefaults.insurance_category, adaptedValues.consultation_language);
  }

  return adaptedValues;
}

function applyFallBacks(formValues: FormValues) {
  const adaptedValues: FormValues = { ...formValues };

  //apply interest fallback on insurance category
  const { interest } = adaptedValues;
  if (!adaptedValues.insurance_category && interest) {
    switch (interest) {
      case Interest.Comprehensive:
        adaptedValues.insurance_category = InsuranceCategory.Full;
        break;
      case Interest.CivilServant:
        adaptedValues.insurance_category = InsuranceCategory.Residual;
        break;
      case Interest.Clinic:
        adaptedValues.insurance_category = InsuranceCategory.Clinic;
        break;
      case Interest.Dental:
        adaptedValues.insurance_category = InsuranceCategory.Dental;
        break;
      default:
        adaptedValues.insurance_category = InsuranceCategory.Unknown;
        break;
    }
  }
  return adaptedValues;
}

function isOfEnum(value: string | undefined, enumerator: object) {
  return Object.values(enumerator).some(enumValue => enumValue === value);
}

export function getCalendarFromCategory(insuranceCategory: InsuranceCategory | undefined, consultationLanguage?: Language): CalendarType {
  if (window.en || consultationLanguage === Language.English) {
    return CalendarType.SalesExpat;
  }
  switch (insuranceCategory) {
    case InsuranceCategory.Dental:
      return CalendarType.SalesDental;
    case InsuranceCategory.Clinic:
      return CalendarType.SalesClinic;
    default:
      return CalendarType.Sales;
  }
}
